import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { Button } from '@components/shadcn/button';
import { Card, CardContent } from '@components/shadcn/card';
import { Progress } from '@components/shadcn/progress';
import { Skeleton } from '@components/shadcn/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/shadcn/tabs';
import { Markdown } from '@components/ui';
import { EmailModal } from '@components/ui/EmailModal/EmailModal';
import { PasswordModal } from '@components/ui/PasswordModal/PasswordModal';
import { useAuditDocumentGenerationMutation } from '@dieterApi/audit/useAuditDocumentGenerationMutation';
import { useGetAuditDocumentQuery } from '@dieterApi/audit/useAuditDocumentQuery';
import { useGetAuditMetadataQuery } from '@dieterApi/audit/useAuditMetadataQuery';
import { useGetAuditSummaryQuery } from '@dieterApi/audit/useAuditSummaryQuery';
import { FileDigit, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Topics } from '../../../context/constants';
import { ChatV2 } from '../ChatV2/ChatV2';
import { ConsumerPanelV2 } from '../OutputView/ConsumerPanelV2';

interface AuditSummaryV2Props {
  applicationId: string;
  setChatBlockedFunc?: (blocked: boolean) => void;
}

export function AuditSummaryV2({ applicationId, setChatBlockedFunc }: AuditSummaryV2Props) {
  const { user, activeCompany } = useUserWithCompany();

  const history = useHistory();

  const { data: summaryData, loading: summaryLoading } = useGetAuditSummaryQuery(applicationId);
  const {
    data: documentData,
    loading: documentLoading,
    refetch: refetchAuditDocument,
  } = useGetAuditDocumentQuery(applicationId);

  const [generateDocument] = useAuditDocumentGenerationMutation();

  const [isInteractionBlocked, setIsInteractionBlocked] = useState(false);

  const topicUnlocked =
    user?.isSubscriber &&
    activeCompany &&
    (['Basic', 'Premium'].includes(activeCompany.subscription) || activeCompany.topicsActivated.includes(Topics.DSE));

  const {
    data: auditMetadata,
    stopPolling: stopPollingAuditMetadata,
    startPolling: startPollingAuditMetadata,
    refetch: refetchAuditMetadata,
  } = useGetAuditMetadataQuery(applicationId);

  useEffect(() => {
    setIsInteractionBlocked(auditMetadata?.getAuditMetadata?.pendingDocumentGeneration ?? false);
  }, [auditMetadata, setIsInteractionBlocked]);

  useEffect(() => {
    if (isInteractionBlocked) {
      console.log('Polling audit metadata due to interaction block.');
      startPollingAuditMetadata(5000);
    } else {
      console.log('Interaction not blocked, stopping audit metadata polling.');
      stopPollingAuditMetadata();
      refetchAuditDocument();
    }
  }, [isInteractionBlocked, startPollingAuditMetadata, stopPollingAuditMetadata, refetchAuditDocument]);

  useEffect(() => {
    setChatBlockedFunc?.(isInteractionBlocked);
  }, [isInteractionBlocked]);

  const isLoading = summaryLoading || documentLoading;
  const { setNavigation } = useNavigation();

  const [startloginFlow, setStartLoginFlow] = useState(false);
  const generateDocumentButtonFunc = () => {
    if (!user?.email || !user?.confirmed) {
      console.log('User not confirmed or email not set, starting login flow.');
      setStartLoginFlow(true);
    } else if (!user?.hasPassword) {
      console.log('User has no password, opening password modal.');
      setNavigation((nav) => void (nav.passwordModalOpen = true));
    } else {
      console.log('Generating document for applicationId:', applicationId);
      setSelectedTab('document');
      generateDocument({
        variables: {
          applicationId: applicationId,
        },
      })
        .then((response) => {
          if (response.data?.auditDocumentGeneration) {
            setIsInteractionBlocked(true);
            console.log('Document generation successful.');
          } else {
            console.error('Failed to generate document.', response);
          }
        })
        .catch((err) => {
          console.error('Error during document generation:', err);
        });
    }
  };

  useEffect(() => {
    if (user?.email && !user?.confirmed) {
      setStartLoginFlow(true);
    } else if (user?.email && user?.confirmed && !user?.hasPassword) {
      setNavigation((nav) => void (nav.passwordModalOpen = true));
      console.log('User has email but no password, opening password modal.');
    }
  }, [user]);

  // Default to 'chat' on mobile, 'summary' on desktop
  const [selectedTab, setSelectedTab] = useState(() => {
    return window.innerWidth < 768 ? 'chat' : 'summary';
  });

  useEffect(() => {
    const handleResize = () => {
      // When switching to desktop, move away from chat tab to avoid duplicate chats
      if (window.innerWidth >= 768 && selectedTab === 'chat') {
        setSelectedTab('summary');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedTab]);
  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    if (value === 'document') {
      refetchAuditDocument();
    }
  };

  return (
    <div className="flex flex-col h-full gap-1 2xl:gap-6 overflow-hidden px-4 md:px-0">
      <Tabs value={selectedTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
        <div className="shrink-0 inline-block pb-1 md:pb-3 mb-1 px-4 2xl:px-20 w-full">
          <TabsList className="w-full justify-center border-0 bg-transparent">
            {/* Show 3 tabs on mobile, 2 on desktop */}
            <div className="flex md:hidden w-full justify-center items-center">
              <TabsTrigger
                value="chat"
                className="flex items-center gap-1 border-0 bg-transparent text-gray-500 data-[state=active]:text-gray-800 data-[state=active]:font-medium data-[state=active]:border-0 data-[state=active]:shadow-none data-[state=active]:bg-transparent text-xs"
              >
                Chat
              </TabsTrigger>
              <div className="text-gray-300 mx-1">|</div>
              <TabsTrigger
                value="summary"
                className="flex items-center gap-1 border-0 bg-transparent text-gray-500 data-[state=active]:text-gray-800 data-[state=active]:font-medium data-[state=active]:border-0 data-[state=active]:shadow-none data-[state=active]:bg-transparent text-xs"
              >
                Preview
              </TabsTrigger>
              <div className="text-gray-300 mx-1">|</div>
              <TabsTrigger
                value="document"
                className="flex items-center gap-1 border-0 bg-transparent text-gray-500 data-[state=active]:text-gray-800 data-[state=active]:font-medium data-[state=active]:border-0 data-[state=active]:shadow-none data-[state=active]:bg-transparent text-xs"
              >
                Dokumentenansicht
              </TabsTrigger>
            </div>
            {/* Show 2 tabs on desktop */}
            <div className="hidden md:flex w-full justify-center items-center">
              <TabsTrigger
                value="summary"
                className="flex items-center gap-2 border-0 bg-transparent text-gray-500 data-[state=active]:text-gray-800 data-[state=active]:font-medium data-[state=active]:border-0 data-[state=active]:shadow-none data-[state=active]:bg-transparent"
              >
                <FileDigit className="h-4 w-4" />
                <span>Summary</span>
              </TabsTrigger>
              <div className="text-gray-300 mx-2">|</div>
              <TabsTrigger
                value="document"
                className="flex items-center gap-2 border-0 bg-transparent text-gray-500 data-[state=active]:text-gray-800 data-[state=active]:font-medium data-[state=active]:border-0 data-[state=active]:shadow-none data-[state=active]:bg-transparent"
              >
                <FileText className="h-4 w-4" />
                <span>Document</span>
              </TabsTrigger>
            </div>
          </TabsList>
        </div>

        <Card
          className={`flex-1 overflow-hidden p-0 ${selectedTab === 'chat' ? 'bg-transparent border-0 shadow-none' : ''}`}
        >
          <CardContent className={`h-full overflow-hidden ${selectedTab === 'chat' ? 'p-0' : 'p-4 2xl:p-10'}`}>
            <TabsContent value="chat" className="h-full m-0 mt-0 overflow-hidden">
              <div
                className="h-full flex flex-col overflow-hidden [&_.bg-card]:bg-transparent!"
                style={{ backgroundColor: 'transparent' }}
              >
                <ChatV2 applicationId={applicationId} disableInput={isInteractionBlocked} />
              </div>
            </TabsContent>

            <TabsContent value="summary" className="h-full m-0 mt-0 overflow-y-auto">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : (
                <div className="prose prose-sm max-w-none font-mono">
                  <Markdown
                    text={
                      summaryData?.getAuditSummary?.detailed ||
                      'No summary available yet. Start the conversation to see your progress here!'
                    }
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="document" className="h-full m-0 mt-0 overflow-y-auto">
              {isLoading || isInteractionBlocked ? (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  <ConsumerPanelV2
                    auditDocumentId={documentData?.getAuditDocument?.id || ''}
                    overlayFunc={() => {
                      // redirect to pricing if topic is not unlocked
                      history.push(`/pricing`);
                    }}
                    shouldCallOverlayFunc={!topicUnlocked}
                  />
                  <Markdown text={documentData?.getAuditDocument?.document || 'No document available.'} />
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {/* Show progress and button only on Preview (summary) and Document tabs, not on Chat */}
      {selectedTab !== 'chat' && (
        <div className="overflow-y-auto shrink-0">
          <Progress
            value={auditMetadata?.getAuditMetadata?.completionPercentage ?? 0}
            bgColor="bg-primary-400"
            className="h-3 my-4"
          />
          <div className="flex justify-center 2xl:mb-4">
            <span className="text-gray-500 font-medium">
              {auditMetadata?.getAuditMetadata?.completionPercentage ?? 0}% completed
            </span>
          </div>

          <hr className="mt-2 mb-4 2xl:my-6 border-gray-200" />

          <div className="grid grid-cols-1 gap-3 2xl:mb-6">
            {isInteractionBlocked && (
              <div className="text-center text-yellow-600">
                <p className="text-sm">Dokumentenerstellung läuft im Hintergrund...</p>
              </div>
            )}
            <Button
              onClick={generateDocumentButtonFunc}
              disabled={!(auditMetadata?.getAuditMetadata?.isAuditComplete ?? false) || isInteractionBlocked}
              variant="outline"
              className="mx-auto py-5 border-2 border-primary-400 rounded-full"
            >
              Dokument Generieren
            </Button>
            {startloginFlow && <EmailModal />}
            {<PasswordModal />}
          </div>
        </div>
      )}
    </div>
  );
}
